var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as React from "react";
import "./ModelUploadDialog.component.css";
import { GenericDialog } from "../generic-dialog/GenericDialog.component";
import { ModelUploadService } from "../../services/model-upload.service";
import { useEffect, useState } from "react";
import { FileSelect } from "../../components/file-input/FileSelect.component";
import { MeshContent } from "./contents/MeshContent.component";
import { MessageService } from "../../services/message.service";
import { RosbagContent } from "./contents/RosbagContent.component";
import { HistoryEventType } from "../../../messages/scripting/ScriptMessages";
import { usersService } from "../../../modules/multiplayer/UsersService";
import { historyEventService } from "../../../modules/multiplayer/peer";
var FileType = ModelUploadService.FileType;
import { useTranslation } from "react-i18next";
function isMeshFileType(fileType) {
    switch (fileType) {
        case FileType.stl:
        case FileType.obj:
        case FileType.gltf:
        case FileType.glb:
        case FileType.fbx:
        case FileType.threemf:
        case FileType.ply:
            return true;
        case FileType.rosbag:
        case FileType.invalid:
        case undefined:
        default:
            return false;
    }
}
export function ModelUploadDialog({ file, onClose }) {
    var _a;
    const [uploadFile, setUploadFile] = useState(undefined);
    const [fileType, setFileType] = useState(undefined);
    const [uploadOptions, setUploadOptions] = useState(undefined);
    const [fileInitialized, setFileInitialized] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const { t } = useTranslation();
    const progressMsgUpload = () => t("upload:progress-msg-upload", "Uploading model...");
    const progressMsgInitRosbag = () => t("upload:progress-msg-init-rosbag", "Initializing Rosbag file...");
    const [progressMessage, setProgressMessage] = useState(progressMsgUpload());
    useEffect(() => {
        if (file) {
            if (ModelUploadService.isFileValid(file)) {
                setUploadFile(file);
            }
            else {
                console.error("File is not valid");
                setUploadFile(undefined);
            }
        }
        else {
            setUploadFile(undefined);
        }
    }, [file]);
    useEffect(() => {
        void ModelUploadService.clearPendingRosbagFiles();
        if (uploadFile !== undefined) {
            const type = ModelUploadService.getFileType(uploadFile);
            setFileType(type);
            if (isMeshFileType(type)) {
                setFileInitialized(true);
            }
            else {
                setFileInitialized(false);
                setProgressMessage(progressMsgInitRosbag());
                setInProgress(true);
                ModelUploadService.prepareRosbagFile(uploadFile)
                    .then(() => {
                    setFileInitialized(true);
                })
                    .catch((error) => {
                    MessageService.showMessage("error", t("upload:error-failed-to-initialize", "{{filename}} failed to initialize: {{error}}", { filename: uploadFile.name, error: error }));
                    setFileInitialized(false);
                })
                    .finally(() => {
                    setInProgress(false);
                    setProgressMessage(progressMsgUpload());
                });
            }
        }
        else {
            setFileInitialized(false);
            setFileType(undefined);
        }
    }, [uploadFile]);
    const performUpload = () => __awaiter(this, void 0, void 0, function* () {
        if (!uploadFile) {
            MessageService.showMessage("error", t("upload:error-no-file", "No file to upload"));
            return;
        }
        if (fileType === undefined || fileType === ModelUploadService.FileType.invalid) {
            MessageService.showMessage("error", t("upload:error-invalid-file-type", "No file type recognized"));
            return;
        }
        if (!uploadOptions) {
            MessageService.showMessage("error", t("upload:error-no-options", "No upload options set"));
            return;
        }
        const uploadFailedMsg = (filename, error) => {
            MessageService.showMessage("error", t("upload:error-upload-failed", "{{filename}} failed to upload: {{error}}", { filename: filename, error: error }));
        };
        ModelUploadService.uploadFile(uploadFile, uploadOptions)
            .then((result) => {
            if (result.success) {
                historyEventService.broadCastHistoryEvent({
                    type: HistoryEventType.modelUpload,
                    value: uploadFile.name,
                    clientId: usersService.getLocalUser().clientId
                });
                MessageService.showMessage("success", t("upload:success-file-uploaded", "{{filename}} uploaded successfully!", { filename: uploadFile.name }));
            }
            else {
                uploadFailedMsg(uploadFile.name, result.error);
            }
        })
            .catch((error) => {
            uploadFailedMsg(uploadFile.name, error);
        });
        onClose();
    });
    return (React.createElement(GenericDialog, { title: t("upload:dialog-title", "Upload Model"), onCancel: onClose, onConfirm: performUpload, confirmAllowed: !!uploadFile && fileType !== ModelUploadService.FileType.invalid && !!uploadOptions, confirmText: t("upload:dialog-button-confirm", "Upload"), confirmTooltipDisabled: t("upload:dialog-button-confirm-tooltip-disabled", "No file selected"), width: "narrow", minWidth: 650, noContentPadding: true, showLoadingSpinner: inProgress, loadingSpinnerText: progressMessage },
        React.createElement("section", { className: "settings-outer-container" },
            React.createElement("section", { className: "model-upload-dialog-file-container settings-container-fixed" },
                React.createElement(FileSelect, { file: file, readonly: !!file && ModelUploadService.isFileValid(file), onChange: setUploadFile, fileTypes: [".stl", ".bag", ".obj", ".glb", ".gltf", ".fbx", ".3mf", ".ply"], tooltip: t("upload:model-file-select-tooltip", "Select a 3D model file from your local computer to upload into this session") })),
            fileType !== ModelUploadService.FileType.invalid && fileInitialized && (React.createElement("section", { className: "settings-container" },
                isMeshFileType(fileType) && fileInitialized && React.createElement(MeshContent, { onChange: setUploadOptions }),
                fileType === ModelUploadService.FileType.rosbag && fileInitialized && (React.createElement(RosbagContent, { onChange: setUploadOptions, fileName: (_a = uploadFile === null || uploadFile === void 0 ? void 0 : uploadFile.name) !== null && _a !== void 0 ? _a : "" })))))));
}
